/* eslint-disable no-useless-escape */
import type { FrapiTeaserItem } from '../../../types/frapi-types.js';
import type { Tag } from '../../../../core/data-sources/frapi/data-types.js';
import type { SortConfig } from '../../layouts/front/sectionHelpers.js';

export type FilterComponent = 'TagCloud' | 'Dropdown' | 'Checkbox';
export type FilterRelationship = 'AND' | 'OR';
export interface FilterMapping {
  relationship: FilterRelationship;
  component: FilterComponent;
  title: string;
  order: number;
}

export interface FilterMappings {
  [key: string]: {
    [key: string]: FilterMapping;
  };
}

export const checkboxOrder = ['Lav', 'Middels', 'Høy'];
export const filterMappings: FilterMappings = {
  'mat-og-drikke': {
    kjøkken: {
      relationship: 'OR',
      component: 'TagCloud',
      title: 'Kjøkken',
      order: 1,
    },
    mattype: {
      relationship: 'OR',
      component: 'TagCloud',
      title: 'Type mat',
      order: 2,
    },
    hererdet: {
      relationship: 'AND',
      component: 'TagCloud',
      title: 'Her er det',
      order: 3,
    },
    område: {
      relationship: 'OR',
      component: 'Dropdown',
      title: 'Områder',
      order: 4,
    },
    pris: {
      relationship: 'OR',
      component: 'Checkbox',
      title: 'Prisnivå',
      order: 5,
    },
  },
  'film-og-serier': {
    type: {
      relationship: 'OR',
      component: 'TagCloud',
      title: 'Sjanger',
      order: 1,
    },
    strømmetjeneste: {
      relationship: 'OR',
      component: 'TagCloud',
      title: 'Strømmetjeneste',
      order: 2,
    },
    format: {
      relationship: 'AND',
      component: 'TagCloud',
      title: 'Format',
      order: 3,
    },
  },
};

export const formatTag = (title: string) => title.split('/')[2];

export const getCategoryTitle = (tag: Tag) => tag.title.split('/')[1];

export const reduceFilters = (filters: Tag[]): Record<string, Tag[]> =>
  filters.reduce((acc: Record<string, Tag[]>, tag) => {
    const categoryTitle = getCategoryTitle(tag);
    acc[categoryTitle] = [...(acc[categoryTitle] || []), tag];

    return acc;
  }, {});

export const generateSearchQuery = (
  activeFilters: Tag[],
  section: string | undefined,
): string => {
  const reducedFilters = reduceFilters(activeFilters);
  const search = new URLSearchParams();

  Object.entries(reducedFilters).forEach(([key, filters]) => {
    const relationship = section && filterMappings[section][key].relationship;

    if (relationship === 'AND') {
      filters.forEach((f) => {
        search.append('tagIds', f.id);
      });
    } else {
      search.append('tagIds', filters.map((f) => f.id).join('|'));
    }
  });

  const str = search.toString();

  return str.length > 0 ? `&${str}` : '';
};

export const generateTagQuery = (activeFilters: Tag[], section: string) => {
  const reducedFilters = reduceFilters(activeFilters);

  let str = '';
  Object.entries(reducedFilters).forEach(([key, filters]) => {
    const relationship = filterMappings[section][key].relationship;

    str +=
      (str ? ',' : '') +
      filters.map((f) => f.id).join(relationship === 'AND' ? ',' : '|');
  });

  return encodeURIComponent(str);
};

export const replacedTagUrl = (address: string, id: string) =>
  address
    .replace(id, '')
    .replace(',|', ',')
    .replace(',,', ',')
    .replace('/,', '/')
    .replace('&tagIds=|', '&tagIds=')
    .replace('&tagIds=&', '&')
    .replace(',?sorting', '?sorting')
    .replace('&tagId=?sorting', '?sorting')
    .replace('/|', '/')
    .replace('|&', '&')
    .replace('|,', ',')
    .replace('||', '|')
    .replace(/\|$/, '') //eol
    .replace(/&tagIds=$/, '') //eol
    .replace(/,$/, ''); //eol

export const orderTeaserTags = (teaserTags: Tag[]) =>
  teaserTags
    .reduce((acc: Tag[], cur) => {
      if (
        !acc.find(
          (item: Tag) => getCategoryTitle(item) === getCategoryTitle(cur),
        )
      ) {
        acc.push(cur);
      }

      return acc;
    }, []) // rreturns only the first occurrence of a type
    .sort((a) => (getCategoryTitle(a) === 'type' ? -1 : 1)); // sorts for 'type' first

export const getSorter = (currentUrl: string): SortConfig =>
  currentUrl.match(/sorting=([^&]*)/)?.[1] as SortConfig;

export const removeDuplicates = (articles: FrapiTeaserItem[]) =>
  Object.values(
    articles.reduce((acc: Record<string, FrapiTeaserItem>, current) => {
      if (acc[current.kicker]) {
        // If the kicker exists, compare the dates of the current and existing object
        if (current.publicationDate > acc[current.kicker].publicationDate) {
          // If the current object is newer, replace the existing object with the current
          acc[current.kicker] = current;
        }
      } else {
        // If it doesn't exist, add the current object to the accumulator
        acc[current.kicker] = current;
      }

      return acc;
    }, {}),
  );
